<template>
  <div>
    <DemoNotice v-if="isDemo" />

    <div class="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">团队成员</h1>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          租户所有者可以管理运营者账号；运营者仅查看列表，不能调整团队账号状态。
        </p>
      </div>
      <span class="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
        {{ authStore.isOwner ? '可管理' : '只读查看' }}
      </span>
    </div>

    <div v-if="statusMessage" class="mb-4 rounded-lg px-4 py-3 text-sm" :class="statusType === 'success'
      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
      : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300'">
      {{ statusMessage }}
    </div>

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div class="xl:col-span-2 rounded-lg bg-white shadow dark:bg-gray-800">
        <div class="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">团队账号列表</h2>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">共 {{ total }} 个账号</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">登录名</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">展示名</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">角色</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">状态</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">最近登录</th>
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="member in members" :key="member.id" class="hover:bg-indigo-50 dark:hover:bg-gray-700/60">
                <td class="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{{ member.loginName }}</td>
                <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ member.displayName }}</td>
                <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ TENANT_CONSOLE_ROLE_LABELS[member.role] }}</td>
                <td class="px-4 py-4 text-sm">
                  <span :class="member.status === 'active'
                    ? 'inline-flex items-center rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300'">
                    {{ member.status === 'active' ? '启用中' : '已停用' }}
                  </span>
                </td>
                <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ member.lastLoginAt ? formatDate(member.lastLoginAt) : '尚未登录' }}</td>
                <td class="px-4 py-4 text-sm">
                  <div class="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      class="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-200 dark:hover:bg-indigo-500/20"
                      :disabled="!authStore.isOwner || busyMemberId === member.id"
                      @click="member.role === 'owner' ? openChangeOwnPasswordModal() : openResetPasswordModal(member)"
                    >
                      {{ member.role === 'owner' ? '修改密码' : '重置密码' }}
                    </button>
                    <button
                      class="cancel-btn"
                      type="button"
                      :disabled="!authStore.isOwner || busyMemberId === member.id || member.role === 'owner'"
                      :title="isDemo ? '演示模式下不可操作' : ''"
                      @click="handleToggleStatus(member.id, member.status)"
                    >
                      {{ member.status === 'active' ? '停用' : '启用' }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!members.length">
                <td colspan="6" class="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">当前还没有团队成员</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="rounded-lg bg-white shadow dark:bg-gray-800">
        <div class="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">新增运营者</h2>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">仅租户所有者可创建运营者账号。</p>
        </div>

        <form class="space-y-4 p-4" @submit.prevent="handleCreateMember">
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">展示名</label>
            <input
              v-model.trim="form.displayName"
              type="text"
              class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              :disabled="!authStore.isOwner || isSubmitting"
            />
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">登录名</label>
            <input
              v-model.trim="form.loginName"
              type="text"
              class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              :disabled="!authStore.isOwner || isSubmitting"
            />
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">初始密码</label>
            <input
              v-model="form.password"
              type="text"
              class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              :disabled="!authStore.isOwner || isSubmitting"
            />
          </div>

          <div class="flex justify-end">
            <button 
              class="primary-btn" 
              type="submit" 
              :disabled="!authStore.isOwner || isSubmitting"
              :title="isDemo ? '演示模式下不可操作' : ''"
            >
              {{ isSubmitting ? '创建中...' : '创建运营者账号' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <Modal :show="showResetPasswordModal" :title="resetPasswordModalTitle" size="small" @close="closeResetPasswordModal">
      <form class="space-y-4" @submit.prevent="handleResetPassword">
        <p class="text-sm leading-6 text-gray-600 dark:text-gray-300">
          将为
          <span class="font-medium text-gray-900 dark:text-gray-100">{{ resetPasswordTarget?.displayName }}</span>
          重新设置登录密码。新密码需至少 8 位。
        </p>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">登录名</label>
          <input
            :value="resetPasswordTarget?.loginName || ''"
            type="text"
            class="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-300"
            disabled
          />
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">新密码</label>
          <input
            v-model="resetPasswordForm.password"
            type="password"
            autocomplete="new-password"
            class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            :disabled="isResettingPassword"
          />
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">确认新密码</label>
          <input
            v-model="resetPasswordForm.confirmPassword"
            type="password"
            autocomplete="new-password"
            class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            :disabled="isResettingPassword"
          />
        </div>

        <div class="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button class="cancel-btn" type="button" :disabled="isResettingPassword" @click="closeResetPasswordModal">取消</button>
          <button class="primary-btn" type="submit" :disabled="isResettingPassword">
            {{ isResettingPassword ? '重置中...' : '确认重置密码' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- 修改密码弹窗 (Owner) -->
    <Modal :show="showChangeOwnPasswordModal" title="修改密码" size="small" @close="closeChangeOwnPasswordModal">
      <form @submit.prevent="handleChangeOwnPassword">
        <div class="space-y-4">
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">当前密码</label>
            <input
              v-model="changeOwnPasswordForm.oldPassword"
              type="password"
              autocomplete="current-password"
              placeholder="请输入当前密码"
              class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">新密码</label>
            <input
              v-model="changeOwnPasswordForm.newPassword"
              type="password"
              autocomplete="new-password"
              placeholder="至少 8 位字符"
              class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">确认新密码</label>
            <input
              v-model="changeOwnPasswordForm.confirmPassword"
              type="password"
              autocomplete="new-password"
              placeholder="再次输入新密码"
              class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button type="button" class="cancel-btn" @click="closeChangeOwnPasswordModal">取消</button>
          <button type="submit" class="primary-btn" :disabled="isChangingOwnPassword">
            {{ isChangingOwnPassword ? '修改中...' : '确认修改' }}
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import {
  changeOwnerPasswordAPI,
  createTeamMemberAPI,
  getTeamMembersAPI,
  resetTeamMemberPasswordAPI,
  updateTeamMemberStatusAPI,
} from "@/api/tenantConsole";
import Modal from "@/components/common/Modal.vue";
import { useAuthStore } from "@/stores/auth";
import { TENANT_CONSOLE_ROLE_LABELS, type TeamMember } from "@/types/tenantConsole";
import DemoNotice from "@/components/DemoNotice.vue";
import toaster from "@/utils/toaster";

const authStore = useAuthStore();
const isDemo = computed(() => authStore.isDemo);
const members = ref<TeamMember[]>([]);
const total = ref(0);
const busyMemberId = ref<number | null>(null);
const isSubmitting = ref(false);
const isResettingPassword = ref(false);
const showResetPasswordModal = ref(false);
const resetPasswordTarget = ref<TeamMember | null>(null);
const showChangeOwnPasswordModal = ref(false);
const isChangingOwnPassword = ref(false);
const statusMessage = ref("");
const statusType = ref<"success" | "error">("success");
const form = reactive({
  displayName: "",
  loginName: "",
  password: "",
});
const resetPasswordForm = reactive({
  password: "",
  confirmPassword: "",
});
const changeOwnPasswordForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const formatDate = (dateTime: string) => new Date(dateTime).toLocaleString("zh-CN");
const resetPasswordModalTitle = computed(() => {
  if (!resetPasswordTarget.value) {
    return "重置密码";
  }
  return `重置密码 · ${resetPasswordTarget.value.displayName}`;
});

const resetResetPasswordForm = () => {
  resetPasswordForm.password = "";
  resetPasswordForm.confirmPassword = "";
};

const resetChangeOwnPasswordForm = () => {
  changeOwnPasswordForm.oldPassword = '';
  changeOwnPasswordForm.newPassword = '';
  changeOwnPasswordForm.confirmPassword = '';
};

const clearResetPasswordState = () => {
  showResetPasswordModal.value = false;
  resetPasswordTarget.value = null;
  resetResetPasswordForm();
};

const openChangeOwnPasswordModal = () => {
  resetChangeOwnPasswordForm();
  showChangeOwnPasswordModal.value = true;
};

const closeChangeOwnPasswordModal = () => {
  showChangeOwnPasswordModal.value = false;
  resetChangeOwnPasswordForm();
};

const closeResetPasswordModal = () => {
  if (isResettingPassword.value) {
    return;
  }

  clearResetPasswordState();
};

const openResetPasswordModal = (member: TeamMember) => {
  if (!authStore.isOwner) {
    return;
  }

  if (isDemo.value) {
    toaster.warning("演示模式下不可操作");
    return;
  }

  resetPasswordTarget.value = member;
  resetResetPasswordForm();
  showResetPasswordModal.value = true;
};

/**
 * 加载团队成员。
 */
const loadMembers = async () => {
  const response = await getTeamMembersAPI({ page: 1, limit: 50 });
  if (response.status !== 200 || !response.data) {
    statusType.value = "error";
    statusMessage.value = "加载团队成员失败";
    return;
  }

  members.value = response.data.accounts;
  total.value = response.data.total;
};

/**
 * 创建运营者账号。
 */
const handleCreateMember = async () => {
  if (!authStore.isOwner || isSubmitting.value) {
    return;
  }

  if (isDemo.value) {
    toaster.warning("演示模式下不可操作");
    return;
  }

  isSubmitting.value = true;
  statusMessage.value = "";
  try {
    const response = await createTeamMemberAPI({
      displayName: form.displayName,
      loginName: form.loginName,
      password: form.password,
    });
    statusType.value = response.status === 200 ? "success" : "error";
    statusMessage.value = response.message || (response.status === 200 ? "创建成功" : "创建失败");
    if (response.status === 200) {
      form.displayName = "";
      form.loginName = "";
      form.password = "";
      await loadMembers();
    }
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * 重置团队成员密码。
 */
const handleResetPassword = async () => {
  if (!authStore.isOwner || !resetPasswordTarget.value || isResettingPassword.value) {
    return;
  }

  if (isDemo.value) {
    toaster.warning("演示模式下不可操作");
    return;
  }

  if (resetPasswordForm.password.length < 8) {
    toaster.error("新密码至少需要 8 位");
    return;
  }

  if (resetPasswordForm.password !== resetPasswordForm.confirmPassword) {
    toaster.error("两次输入的密码不一致");
    return;
  }

  isResettingPassword.value = true;
  busyMemberId.value = resetPasswordTarget.value.id;
  statusMessage.value = "";
  try {
    const response = await resetTeamMemberPasswordAPI(resetPasswordTarget.value.id, {
      password: resetPasswordForm.password,
    });
    statusType.value = response.status === 200 ? "success" : "error";
    statusMessage.value = response.message || (response.status === 200 ? "重置密码成功" : "重置密码失败");
    if (response.status === 200) {
      toaster.success("重置密码成功");
      clearResetPasswordState();
      await loadMembers();
    }
  } finally {
    isResettingPassword.value = false;
    busyMemberId.value = null;
  }
};

/**
 * 切换团队成员状态。
 */
const handleToggleStatus = async (memberId: number, status: TeamMember["status"]) => {
  if (isDemo.value) {
    toaster.warning("演示模式下不可操作");
    return;
  }

  busyMemberId.value = memberId;
  statusMessage.value = "";
  try {
    const nextStatus = status === "active" ? "suspended" : "active";
    const response = await updateTeamMemberStatusAPI(memberId, nextStatus);
    statusType.value = response.status === 200 ? "success" : "error";
    statusMessage.value = response.message || "更新成员状态失败";
    if (response.status === 200) {
      await loadMembers();
    }
  } finally {
    busyMemberId.value = null;
  }
};

/**
 * 修改所有者自己的密码。
 */
const handleChangeOwnPassword = async () => {
  if (!authStore.isOwner || isChangingOwnPassword.value) {
    return;
  }

  if (isDemo.value) {
    toaster.warning("演示模式下不可操作");
    return;
  }

  // Validation
  if (!changeOwnPasswordForm.oldPassword) {
    toaster.error('请输入当前密码');
    return;
  }

  if (changeOwnPasswordForm.newPassword.length < 8) {
    toaster.error('新密码至少需要 8 位字符');
    return;
  }

  if (changeOwnPasswordForm.newPassword === changeOwnPasswordForm.oldPassword) {
    toaster.error('新密码不能与当前密码相同');
    return;
  }

  if (changeOwnPasswordForm.newPassword !== changeOwnPasswordForm.confirmPassword) {
    toaster.error('两次输入的新密码不一致');
    return;
  }

  isChangingOwnPassword.value = true;

  try {
    const response = await changeOwnerPasswordAPI({
      oldPassword: changeOwnPasswordForm.oldPassword,
      newPassword: changeOwnPasswordForm.newPassword,
    });

    if (response.status === 200) {
      toaster.success('密码修改成功');
      closeChangeOwnPasswordModal();
      return;
    }

    toaster.error(response.message || '密码修改失败');
  } catch (error) {
    console.error('Change own password error:', error);
    toaster.error('密码修改失败');
  } finally {
    isChangingOwnPassword.value = false;
  }
};

onMounted(() => {
  loadMembers();
});
</script>
